/**
 * Learning Center app (feature_key 'lms'; plan 4.1, 4.2, 4.8, section 13).
 *
 * Dark until the owner activates it (plan 4.1, section 16): the features row
 * ships is_active=false and nobody holds the entitlement, so every
 * non-super_admin sees the "Access Denied" screen below until then. Access is
 * the site's lms entitlement (GET /api/my-tools, tool_id 'lms',
 * is_available === true), super_admin bypassing it; the eReg and contracts
 * gate shape. Inside the module the two admin tabs follow GET /api/lms/me
 * (isLmsAdmin, plan 4.2), and every admin route is enforced server-side too.
 *
 * The entitlement check and the Layout are hoisted ABOVE <Routes> (the
 * InsightsApp and ContractsApp pattern) so the shell mounts once per module
 * visit and switching tabs never re-runs the check. The check re-runs on
 * every site switch (the entitlement is per site) and ignores an answer for a
 * site that is no longer current.
 *
 * LmsProvider lives in App.tsx's ModuleProviders (plan 4.8), so site context
 * persists across cross-module navigation. ToastProvider is mounted here,
 * once for the module shell, so every screen can call useToast (section 13).
 *
 * Hot file (plan 15.12): WP-03 only. A tab WP replaces the file a route below
 * imports (keeping its default export) and never edits this one. The one
 * authorised later edit is WP-48's (plan 18.5): ProtectedRoute keeps the site
 * its verdict belongs to and shows the Spinner while that differs from the
 * current site, so the shell never renders (and never fires GET /api/lms/me)
 * for a newly switched site before its own entitlement verdict.
 */
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import EmptyState from '../ui/EmptyState';
import { ToastProvider } from '../ui/Toast';
import { Layout } from './components/layout/Layout';
import { useLmsMe } from './hooks/useLmsMe';
import { LMS_TABS } from './tabs/config';
import type { LmsTabKey } from './tabs/config';
import HomeTab from './tabs/home/HomeTab';
import CoursesTab from './tabs/courses/CoursesTab';
import CoursePage from './tabs/courses/CoursePage';
import LearnPage from './tabs/courses/LearnPage';
import RecordsTab from './tabs/records/RecordsTab';
import SopsTab from './tabs/sops/SopsTab';
import OnboardingTab from './tabs/onboarding/OnboardingTab';
import TeamTab from './tabs/team/TeamTab';
import ManageTab from './tabs/manage/ManageTab';

/** The screen each tab key routes to (tabs/config.ts holds the metadata, this map the components). */
const TAB_SCREENS: Record<LmsTabKey, ComponentType> = {
  home: HomeTab,
  courses: CoursesTab,
  records: RecordsTab,
  sops: SopsTab,
  onboarding: OnboardingTab,
  team: TeamTab,
  manage: ManageTab,
};

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div
        role="status"
        aria-label="Loading"
        className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400"
      />
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, currentSite, sitesLoading, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);
  // The site the verdict above belongs to (undefined: no verdict yet). On a site
  // switch the first render still holds the previous site's verdict, before the
  // effect below re-enters the checking state; children's effects run before
  // this component's, so rendering the shell then would fire GET /api/lms/me for
  // a site nobody has checked (plan 18.5). The Spinner covers that render.
  const [verdictSiteId, setVerdictSiteId] = useState<string | null | undefined>(undefined);

  // The entitlement is per SITE, so this effect re-runs on every site switch
  // and re-enters the checking state at the top; otherwise the previous
  // site's verdict would keep rendering while the new check is in flight.
  // `cancelled` keeps a slow answer for an abandoned site from landing on top
  // of a newer one.
  useEffect(() => {
    let cancelled = false;
    const forSite = currentSite?.id ?? null;

    const checkEntitlement = async () => {
      if (isSuperAdmin) {
        if (cancelled) return;
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setVerdictSiteId(forSite);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setVerdictSiteId(forSite);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools: Array<{ tool_id?: string; is_available?: boolean }> = Array.isArray(response.data)
          ? response.data
          : [];
        const lmsTool = tools.find((t) => t.tool_id === 'lms');
        if (cancelled) return;
        setHasEntitlement(lmsTool?.is_available === true);
        setEntitlementChecked(true);
        setVerdictSiteId(forSite);
      } catch (error) {
        console.error('Error checking Learning Center entitlement:', error);
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setVerdictSiteId(forSite);
      } finally {
        if (!cancelled) setCheckingEntitlement(false);
      }
    };

    if (session && !sitesLoading) {
      setCheckingEntitlement(true);
      checkEntitlement();
    }

    return () => {
      cancelled = true;
    };
  }, [session, isSuperAdmin, currentSite, sitesLoading]);

  // Before the spinner: the effect only runs with a session, so a session-less
  // visit would otherwise spin forever instead of redirecting.
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (sitesLoading || checkingEntitlement || verdictSiteId !== (currentSite?.id ?? null)) {
    return <Spinner />;
  }

  if (entitlementChecked && !hasEntitlement && !isSuperAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <div className="max-w-md p-8 text-center">
          <h1 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Access Denied</h1>
          <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
            You do not have access to the Learning Center for your current site. Please contact your administrator if
            you believe this is an error.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // super_admin passes the entitlement check without a site; the module
  // still needs one, because every /api/lms call carries X-Site-Id.
  if (!currentSite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <EmptyState
          icon={Building2}
          title="No site selected"
          hint="Select a site from the header to use the Learning Center."
        />
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * The admin tabs' route guard (plan section 13): renders the tab once
 * GET /api/lms/me says isLmsAdmin, waits while it loads (so an admin's deep
 * link or reload is not bounced), and otherwise sends the person Home.
 */
function AdminOnly({ children }: { children: ReactNode }) {
  const { loading, isLmsAdmin } = useLmsMe();
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div
          role="status"
          aria-label="Loading"
          className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400"
        />
      </div>
    );
  }
  if (!isLmsAdmin) return <Navigate to="/lms" replace />;
  return <>{children}</>;
}

function LmsRoutes() {
  return (
    <Routes>
      {LMS_TABS.map((tab) => {
        const Screen = TAB_SCREENS[tab.key];
        const element = tab.adminOnly ? (
          <AdminOnly>
            <Screen />
          </AdminOnly>
        ) : (
          <Screen />
        );
        if (tab.path === '') return <Route key={tab.key} index element={element} />;
        return <Route key={tab.key} path={tab.hasSubviews ? `${tab.path}/*` : tab.path} element={element} />;
      })}
      {/* plan section 13 tab 2 and 7.2: the course page (WP-42) and the player (WP-40). */}
      <Route path="courses/:courseId" element={<CoursePage />} />
      <Route path="courses/:courseId/learn" element={<LearnPage />} />
      <Route path="*" element={<Navigate to="/lms" replace />} />
    </Routes>
  );
}

export function LmsApp() {
  return (
    <ProtectedRoute>
      <ToastProvider>
        <Layout>
          <LmsRoutes />
        </Layout>
      </ToastProvider>
    </ProtectedRoute>
  );
}

export default LmsApp;
